import { EventEmitter } from "events";
import { VisibilityGroup } from "threngine";
// import { sleep } from "../ulti";
import { Controller3D } from "./3DController";
import { AnimationDirection } from "./AnimationController";
import { CarModelCameraList, CarModelConfig, ModelLoadController, CarTrunkLoading } from "./ModelLoad/ModelLoadController";
import {trackVisualFeature} from "../Tracking";
import { Object3D, Mesh } from "three";
import { nodeHasVisibleMeshes, sleep } from "../ulti";
import { EventController } from "./Event/EventController";

export enum TrunkCargo {
  None,
  Boxes = "boxes",
  Shopping = "shopping",
  Travelling = "travelling"
}

export class TrunkLoadingController {
  private _active:boolean = false;
  private forcedOpenTrunkLid:boolean = false;
  private _currentCargo:TrunkCargo = TrunkCargo.None;
  private _outsideCargo:TrunkCargo = TrunkCargo.None;
  private config:CarTrunkLoading;
  private currentVG:VisibilityGroup|undefined;
  private _blocked:boolean = false;
  private firstLoadCargo:boolean = false;
  private loadingPromise:Promise<void>|null = null;

  public get active() { return this._active; }
  public get blocked() { return this._blocked; }
  public get currentCargo():TrunkCargo { return this._currentCargo; }
  public set currentCargo(value:TrunkCargo) { this.loadCargo(value); }

  public static event:EventEmitter = new EventEmitter();

  public static CARGO_CHANGED:string = "cargo_changed";

  public constructor(
    public controller3D:Controller3D,
    private eventController:EventController
  ) {
    ModelLoadController.event.addListener(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this._active = false;
      this.forcedOpenTrunkLid = false;
      this._currentCargo = TrunkCargo.None;
      this.currentVG = undefined;
      this.config = config.trunkLoading;
    });
  }

  public getCurrentModelSupportsCargo(cargo:TrunkCargo):boolean {
    return this.config[cargo] !== undefined;
  }

  private getDefaultCargo():TrunkCargo {
    // TODO: allow setting an explicit default in the car JSON:
    let cargo = <TrunkCargo>this.eventController.getDefaultTrunkCargo() || TrunkCargo.Shopping;

    for (let i = 0; i < 2; i++) {
      if (this.getCurrentModelSupportsCargo(cargo)) break;

           if (cargo == TrunkCargo.Boxes) cargo = TrunkCargo.Shopping;
      else if (cargo == TrunkCargo.Shopping) cargo = TrunkCargo.Travelling;
      else if (cargo == TrunkCargo.Travelling) cargo = TrunkCargo.Boxes;
    }

    if (!this.getCurrentModelSupportsCargo(cargo)) {
      throw `TrunkLoadingController: no cargos are supported by this car`
    }

    return cargo;
  }

  public async enter():Promise<void> {
    this._active = true;

    this.forcedOpenTrunkLid = !this.controller3D.trunkLidOpen;
    this.firstLoadCargo = true;
    if (this.currentCargo == TrunkCargo.None) {
      this.currentCargo = this.getDefaultCargo();
    }
    this.loadCargo(this.currentCargo);

    await this.controller3D.setCamera(CarModelCameraList.TrunkLoading);
    await this.openTrunkLid();
  }

  public async leave():Promise<void> {
    const configurator = this.controller3D.engine.configurator;
    
    if (!this._active) {
      return;
    }

    this.closeTrunkLid();

    if (this.currentVG) {
      await configurator.setVisible(this.currentVG.grouping_ids, false);
      this.currentVG = undefined;
    }

    this._active = false;

    await this.controller3D.setCamera(CarModelCameraList.Orbit);
  }

  public async getTrunkInfo():Promise<any> {
    if (this.loadingPromise) {
      await this.loadingPromise;
    }

    // HACK: for some reason after waiting for setVisible() the meshes aren't visible immediately?
    // Investigate this more...
    await sleep(32);

    switch (this.currentCargo) {
      case TrunkCargo.None: return {};
      case TrunkCargo.Shopping: return this.getInfoShopping();
      case TrunkCargo.Boxes: return this.getInfoBoxes();
      case TrunkCargo.Travelling: return this.getInfoTravelling();
    }
  }

  public async loadCargo(cargo:TrunkCargo, track?:boolean, interact3D?:boolean):Promise<void> {
    const animationController = this.controller3D.animationController;
    const configurator = this.controller3D.engine.configurator;
    const scene = this.controller3D.engine.scene;
    const oldCargo = this._currentCargo;

    if (!this._active || this._blocked || (this._currentCargo == cargo && !this.firstLoadCargo)) {
      return; 
    }

    if(track){
      this.track(oldCargo, cargo, interact3D);
    }

    this._blocked = true;
    this._currentCargo = cargo;
    this._outsideCargo = TrunkCargo.None;

    TrunkLoadingController.event.emit(TrunkLoadingController.CARGO_CHANGED, cargo);

    if ((oldCargo != TrunkCargo.None && cargo != TrunkCargo.None) || this.firstLoadCargo) {
      // Switch (or first load)
      this.firstLoadCargo = false;

      if (this.currentVG) {
        configurator.setVisible(this.currentVG.grouping_ids, false);  
      }
      
      const entry = this.config[cargo];
      if (!entry) {
        throw `Cannot find config for cargo: ${TrunkCargo[cargo]}`;
      }
  
      this.currentVG = configurator.config.visibility_groups.find(g => g.name === entry.visibilityGroup);
      if (!this.currentVG) {
        throw 'Failed to find visibility group for trunk loading';
      }

      await (this.loadingPromise = configurator.setVisible(this.currentVG.grouping_ids, true));

      const root = scene.getObjectByName(entry.animationRoot);
      if (!root) {
        throw `Failed to find animation root: ${entry.animationRoot}`;
      }
      animationController.forceAnimationsPositionByAncestorObject(root, true);
    } else if (cargo != TrunkCargo.None) {
      // Loading

      await this.openTrunkLid();

      const entry = this.config[cargo];
      if (!entry) {
        throw `Cannot find config for cargo: ${TrunkCargo[cargo]}`;
      }

      const nextVG = configurator.config.visibility_groups.find(g => g.name === entry.visibilityGroup);
      if (!nextVG) {
        throw 'Failed to find visibility group for trunk loading';
      }

      if (this.currentVG != nextVG) {
        if (this.currentVG) {
          configurator.setVisible(this.currentVG.grouping_ids, false);  
        }

        this.currentVG = nextVG;

        await (this.loadingPromise = configurator.setVisible(nextVG.grouping_ids, true));
      }

      const root = scene.getObjectByName(entry.animationRoot);
      if (!root) {
        throw `Failed to find animation root: ${entry.animationRoot}`;
      }

      await animationController.playAnimationsByAncestorObject(root, AnimationDirection.Forward);
    } else {
      // Unloading

      this._outsideCargo = oldCargo;

      await this.openTrunkLid();

      const entry = this.config[oldCargo];
      if (!entry) {
        throw `Cannot find config for cargo: ${TrunkCargo[oldCargo]}`;
      }

      const root = scene.getObjectByName(entry.animationRoot);
      if (!root) {
        throw `Failed to find animation root: ${entry.animationRoot}`;
      }
      await animationController.playAnimationsByAncestorObject(root, AnimationDirection.Backward);
    }

    this._blocked = false;
  }

  public isHiddenTrigger(name:string):boolean {
    if (!this.active || this.blocked) {
      return false;
    } 

    if (this.currentCargo != TrunkCargo.None) {
      return name === this.config.unloadTrigger;
    } else {
      switch (this._outsideCargo) {
        case TrunkCargo.Shopping: {
          return name === this.config.shopping.trigger;
        }
        case TrunkCargo.Travelling: {
          return name === this.config.travelling.trigger;
        }
        case TrunkCargo.Boxes: {
          return name === this.config.boxes.trigger;
        }
      }
    }
  
    return false;
  }

  public onSelection(name:string):boolean {
    if (name === this.config.unloadTrigger) {
      this.loadCargo(TrunkCargo.None, true, true);
      return true;
    }

    if (this.config.shopping && name === this.config.shopping.trigger) {
      this.loadCargo(TrunkCargo.Shopping, true, true);
      return true;
    }

    if (this.config.travelling && name === this.config.travelling.trigger) {
      this.loadCargo(TrunkCargo.Travelling, true, true);
      return true;
    }

    if (this.config.boxes && name === this.config.boxes.trigger) {
      this.loadCargo(TrunkCargo.Boxes, true, true);
      return true;
    }

    return false;
  }

  private getInfoShopping():any {
    const NUMBER_OF_BOTTLES_IN_CARRIER = 6;

    return {
      bottles: this.countNumberOfChildren(this.config.shopping.bottleRoot) * NUMBER_OF_BOTTLES_IN_CARRIER,
      bags: this.countNumberOfChildren(this.config.shopping.bagRoot)
    };
  }

  private getInfoTravelling():any {
    const MAX_OBJECT_COUNT = 9;
    const MIN_OBJECT_COUNT = 3;

    const objectCount = this.countNumberOfChildren(this.config.travelling.loadRoot);
    const rating = Math.max(
      Math.min(
        1 + 4 * (objectCount - MIN_OBJECT_COUNT) / (MAX_OBJECT_COUNT - MIN_OBJECT_COUNT), 5)
      , 1) | 0;

    return {
      rating: rating
    };
  }

  private getInfoBoxes():any {
    return {
      boxS: this.countNumberOfChildren(this.config.boxes.boxRootS),
      boxM: this.countNumberOfChildren(this.config.boxes.boxRootM),
      boxL: this.countNumberOfChildren(this.config.boxes.boxRootL),
    };
  }

  private countNumberOfChildren(rootName:string):number {
    const root = this.controller3D.engine.scene.getObjectByName(rootName);
    if (!root) {
      throw `Cannot find object: ${rootName}`;
    }

    return root.children.filter(c => nodeHasVisibleMeshes(c)).length;
  }

  private async openTrunkLid():Promise<void> {
    if (!this.controller3D.trunkLidOpen) {
      await this.controller3D.playTrunkLidAnimation();
    }
  }

  private async closeTrunkLid():Promise<void> {
    if (this.controller3D.trunkLidOpen && this.forcedOpenTrunkLid) {
      return this.controller3D.playTrunkLidAnimation();
    }

    return Promise.resolve();
  }

  private track(oldCargo:TrunkCargo, newCargo:TrunkCargo, interact3D:boolean):void {
    switch (newCargo){
      case TrunkCargo.None: {
        switch (oldCargo) {
          case TrunkCargo.Shopping:
            trackVisualFeature("unload shopping");
            break;
          case TrunkCargo.Boxes:
            trackVisualFeature("unload boxes", interact3D)
            break;
          case TrunkCargo.Travelling:
            trackVisualFeature("unload travelling", interact3D)
            break;
        }
        break;
      }
      case TrunkCargo.Shopping: {
        trackVisualFeature("load shopping", interact3D);
        break;
      }
      case TrunkCargo.Boxes: {
        trackVisualFeature("load boxes", interact3D)
        break;
      }
      case TrunkCargo.Travelling: {
        trackVisualFeature("load travelling", interact3D)
        break;
      }
    }
  }
}